import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import { Quote, Trophy, Flame, Target, Heart, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 420, height: 420, background: "#15FF73", top: -120, right: -100 }} />
      <div className="blob" style={{ width: 320, height: 320, background: "#FF1A8C", top: 600, left: -80, animationDelay: "-6s" }} />
      <NavBar />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-12 pb-12 grid lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7">
          <span className="sticker sticker-magenta mb-4 inline-block">Founder · The Story</span>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[104px] font-extrabold leading-[0.88] tracking-[-0.04em] mt-3 mb-6">
            Built by a <span className="text-rainbow">competitor</span>.<br />
            For the <span className="italic squiggle">culture</span>.
          </h1>
          <p className="text-lg text-[#0B0F0C]/70 max-w-xl leading-relaxed mb-3">
            X-Seed wasn't built in a boardroom. It was built courtside, late nights, between practices and pop-ups —
            by someone tired of watching the homies overpay for mid.
          </p>
          <p className="text-base text-[#0B0F0C]/60 max-w-xl leading-relaxed">
            <span className="font-script text-2xl text-[#FF1A8C]">— and that's how it started.</span>
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/discover" className="btn-x btn-primary">Find the gas <ArrowRight size={16} /></Link>
            <Link to="/pitch" className="btn-x btn-ghost">For shops →</Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="card-x card-green bg-[#FAF6E8] overflow-hidden tilt rotate-[-2deg]">
              <img src="/img/owner.jpg" alt="X-Seed founder · anonymous silhouette" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-4 -right-4 sticker sticker-lemon !text-sm rotate-[8deg] shadow-[3px_3px_0_#0B0F0C]">XN</div>
            <div className="absolute -bottom-4 -left-4 sticker sticker-magenta !text-sm rotate-[-6deg] shadow-[3px_3px_0_#0B0F0C]">FOUNDER</div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="surface-ink border-y-2 border-[#0B0F0C] py-20 relative overflow-hidden">
        <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -80, left: "30%", opacity: 0.25 }} />
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center relative">
          <Quote size={40} strokeWidth={1.5} className="mx-auto mb-6 text-[#15FF73]" />
          <blockquote className="font-display text-3xl sm:text-5xl font-extrabold text-[#F5F0DC] leading-[1.05] tracking-tight">
            "I came up an <span className="text-[#15FF73]">athlete</span>. We tracked every stat,
            every drill, every <em>second</em>. So why does a <span className="italic text-rainbow">$60 eighth</span>
            cost $35 down the block — and nobody's measuring?"
          </blockquote>
          <div className="mt-7 font-script text-3xl text-[#FF1A8C]">— the founder, X-Seed</div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 relative">
        <span className="sticker sticker-cyan mb-3 inline-block">The Story · 3 Chapters</span>
        <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.95] mt-3 mb-12">From <span className="text-green-gradient italic">jersey #15</span><br />to deal #1.</h2>

        <div className="grid lg:grid-cols-3 gap-6 mb-14">
          <div className="card-x card-green bg-[#FAF6E8] p-7">
            <div className="flex items-center gap-3 mb-4"><Trophy size={22} className="text-[#15FF73]" /><span className="label-overline text-[#FF1A8C]">Chapter 1 · Discipline</span></div>
            <h3 className="font-display text-2xl font-extrabold mb-3 leading-tight">Train. Track. Repeat.</h3>
            <p className="text-sm text-[#0B0F0C]/70 leading-relaxed">
              Years of competitive sport. Numbers on every rep. Stats on every play. That obsession
              with measurement didn't go away when the cleats came off — it just looked for a new
              field.
            </p>
          </div>
          <div className="card-x card-magenta bg-[#FAF6E8] p-7">
            <div className="flex items-center gap-3 mb-4"><Target size={22} className="text-[#FF1A8C]" /><span className="label-overline text-[#FF1A8C]">Chapter 2 · The "why"</span></div>
            <h3 className="font-display text-2xl font-extrabold mb-3 leading-tight">Mid was winning.</h3>
            <p className="text-sm text-[#0B0F0C]/70 leading-relaxed">
              Walked into a shop. Same eighth, three different prices, three different blocks.
              Reviews? Either spam or silent. Realized: in the most-tracked retail category in the world,
              cannabis pricing was still vibes-based. Wild.
            </p>
          </div>
          <div className="card-x card-cyan bg-[#FAF6E8] p-7">
            <div className="flex items-center gap-3 mb-4"><Flame size={22} className="text-[#FF7A3D]" /><span className="label-overline text-[#FF1A8C]">Chapter 3 · The build</span></div>
            <h3 className="font-display text-2xl font-extrabold mb-3 leading-tight">Score every $.</h3>
            <p className="text-sm text-[#0B0F0C]/70 leading-relaxed">
              Wrote the algorithm: price-per-gram × potency × rating. Built the QR loop so shops
              pay on results. Added GAS / PASS so the people scoring it were the ones smoking it.
              X-Seed shipped.
            </p>
          </div>
        </div>

        {/* FOUNDER PHOTO 2 + CARD */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="card-x card-magenta bg-[#FAF6E8] p-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-full bg-[#FF1A8C] border-2 border-[#0B0F0C] flex items-center justify-center text-white font-display text-2xl font-extrabold">XN</div>
                <div>
                  <div className="font-display text-2xl font-extrabold leading-none">XN</div>
                  <div className="font-mono-x text-[10px] font-bold uppercase tracking-widest text-[#0B0F0C]/55">Anonymous · By design</div>
                </div>
              </div>
              <p className="text-sm text-[#0B0F0C]/70 leading-relaxed mb-4">
                You don't need to know who built X-Seed. The math doesn't care. The connoisseur
                doesn't care. <em className="font-bold">The mid does</em>—and that's the only audience
                I'm trying to lose.
              </p>
              <p className="text-sm text-[#0B0F0C]/70 leading-relaxed mb-5">
                X-Seed isn't another listings app — it's a <em className="font-bold">measurement system</em>.
                Built so the consumer wins, the shop wins, and the mid loses. The work speaks.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="sticker sticker-green">Connoisseur</span>
                <span className="sticker sticker-cyan">Anonymous</span>
                <span className="sticker sticker-lemon">Builder</span>
                <span className="sticker sticker-sunset">21+</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative">
              <div className="card-x card-green bg-[#FAF6E8] overflow-hidden">
                <img src="/img/owner.jpg" alt="Founder · silhouette" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -top-3 left-6 sticker sticker-lemon !text-sm rotate-[-6deg] shadow-[3px_3px_0_#0B0F0C]">XN</div>
              <div className="absolute -bottom-3 right-6 sticker sticker-magenta !text-sm rotate-[4deg] shadow-[3px_3px_0_#0B0F0C]">"identity stays in the shadows"</div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="surface-deepgreen border-y-2 border-[#0B0F0C] py-20 relative overflow-hidden">
        <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", bottom: -80, right: -60, opacity: 0.3 }} />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center relative">
          <Heart size={32} className="mx-auto mb-5 text-[#FF1A8C]" fill="currentColor" />
          <span className="sticker sticker-magenta mb-4 inline-block">The Mission</span>
          <h2 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.95] text-[#F5F0DC] mt-3 mb-6">
            Make sure the <span className="text-[#15FF73]">people</span> never overpay.<br />
            Make sure the <span className="text-[#15FF73]">shops</span> never overspend.
          </h2>
          <p className="text-lg text-[#F5F0DC]/70 max-w-2xl mx-auto leading-relaxed">
            X-Seed exists so good shops grow on results, not budgets — and so the connoisseur
            never has to gamble with their wallet again. Closed loop. Measured. Real.
          </p>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-20">
        <WaitlistForm
          kicker="Be the first"
          title="Drops in your city. Soon."
          note="We're scaling state-by-state. Drop your email + city — we'll alert you when X-Seed lights up your zip."
        />
      </section>

      <Footer />
    </div>
  );
}
